import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import AuthContext from '../../context/authContext';
import EditableList from '../EditableList/EditableList';
import useAuditLog from '../../hooks/useAuditLog';
import useUser from '../../hooks/useUser';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE');
}

function formatUserDisplayName(user) {
  if (!user || typeof user !== 'object') return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (name && user.username) return `${name} (${user.username})`;
  return user.username || name || '';
}

function AuditLogView() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const authCtx = useContext(AuthContext);
  const { getAuditLogById } = useAuditLog();
  const { fetchUsers } = useUser();

  const [searchField, setSearchField] = useState('action');
  const [levelFilter, setLevelFilter] = useState('');
  const [actorTypeFilter, setActorTypeFilter] = useState('');
  const [impersonatedFilter, setImpersonatedFilter] = useState('');
  const [listParams, setListParams] = useState({ limit: 50 });
  const [detailDialog, setDetailDialog] = useState({ open: false, item: null, data: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [userNameMap, setUserNameMap] = useState({});

  const searchInputRef = useRef(null);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const fetchUsersRef = useRef(fetchUsers);

  useEffect(() => {
    fetchUsersRef.current = fetchUsers;
  }, [fetchUsers]);

  useEffect(() => {
    let isMounted = true;

    const loadUserNames = async () => {
      try {
        const response = await fetchUsersRef.current();
        const users = Array.isArray(response) ? response : Array.isArray(response?.users) ? response.users : [];
        if (!isMounted || !Array.isArray(users)) return;
        const nextMap = {};
        users.forEach(user => {
          const id = user?._id ? String(user._id) : '';
          if (!id) return;
          nextMap[id] = formatUserDisplayName(user);
        });
        setUserNameMap(nextMap);
      } catch (_) {
        if (!isMounted) return;
        setUserNameMap({});
      }
    };

    loadUserNames();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!authCtx.checkRight('auditLog:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const applyFilters = useCallback(() => {
    const searchValue = searchInputRef.current?.value?.trim() || '';
    const fromValue = fromInputRef.current?.value || '';
    const toValue = toInputRef.current?.value || '';

    const nextParams = { limit: 50 };

    if (searchValue) {
      nextParams[searchField] = searchValue;
    }
    if (levelFilter) {
      nextParams.level = levelFilter;
    }
    if (actorTypeFilter) {
      nextParams.actorType = actorTypeFilter;
    }
    if (impersonatedFilter) {
      nextParams.impersonated = impersonatedFilter;
    }
    if (fromValue) {
      nextParams.from = new Date(`${fromValue}T00:00:00`).toISOString();
    }
    if (toValue) {
      nextParams.to = new Date(`${toValue}T23:59:59`).toISOString();
    }

    setListParams(nextParams);
  }, [searchField, levelFilter, actorTypeFilter, impersonatedFilter]);

  const resetFilters = useCallback(() => {
    if (searchInputRef.current) searchInputRef.current.value = '';
    if (fromInputRef.current) fromInputRef.current.value = '';
    if (toInputRef.current) toInputRef.current.value = '';
    setSearchField('action');
    setLevelFilter('');
    setActorTypeFilter('');
    setImpersonatedFilter('');
    setListParams({ limit: 50 });
  }, []);

  const openDetails = useCallback(
    async item => {
      if (!item?._id) return;
      setDetailDialog({ open: true, item, data: null });
      setDetailLoading(true);
      try {
        const data = await getAuditLogById(item._id, { includePatch: true, includeDiff: true });
        setDetailDialog({ open: true, item, data });
      } finally {
        setDetailLoading(false);
      }
    },
    [getAuditLogById]
  );

  const closeDetails = useCallback(() => {
    setDetailDialog({ open: false, item: null, data: null });
  }, []);

  const formatActorDisplay = useCallback(
    (actorType, actorId) => {
      const id = actorId ? String(actorId) : '';
      if (!id) return '—';

      if (actorType === 'user') {
        const userLabel = userNameMap[id];
        return userLabel ? `${userLabel} [${id}]` : id;
      }
      if (actorType === 'service') {
        const serviceName = id.startsWith('service:') ? id.slice('service:'.length) : id;
        return serviceName ? `Service (${serviceName})` : 'Service';
      }
      if (actorType === 'system') return 'System';
      if (actorType === 'migration') return 'Migration';
      return id;
    },
    [userNameMap]
  );

  // Name without the trailing id — used where one cell has to carry two people
  // (the switching user and the identity they acted under).
  const formatUserShort = useCallback(
    userId => {
      const id = userId ? String(userId) : '';
      if (!id) return '—';
      return userNameMap[id] || id;
    },
    [userNameMap]
  );

  // An entry from a session created via "Identität ändern" names the person who
  // acted first and the borrowed identity below it.
  const formatActorCell = useCallback(
    item => {
      const actorLabel = formatActorDisplay(item?.actorType, item?.actorId);
      if (!item?.impersonatorId) return actorLabel;
      return `${formatUserShort(item.impersonatorId)}\nim Namen von ${formatUserShort(item.actorId)}`;
    },
    [formatActorDisplay, formatUserShort]
  );

  const auditLogSchema = useMemo(
    () => ({
      createdAt: {
        label: 'Zeitpunkt',
        api: { itemField: 'createdAt' },
        type: 'text',
        readOnly: true,
        minWidth: '180px',
        listDisplayValue: value => formatDateTime(value),
      },
      level: {
        label: 'Level',
        api: { itemField: 'level' },
        type: 'select',
        readOnly: true,
        options: [
          { value: 1, label: '1' },
          { value: 2, label: '2' },
          { value: 3, label: '3' },
        ],
        minWidth: '90px',
      },
      action: {
        label: 'Aktion',
        api: { itemField: 'action' },
        type: 'text',
        readOnly: true,
        minWidth: '220px',
      },
      entityType: {
        label: 'Entity',
        api: { itemField: 'entityType' },
        type: 'text',
        readOnly: true,
        minWidth: '130px',
      },
      entityId: {
        label: 'Entity-ID',
        api: { itemField: 'entityId' },
        type: 'text',
        readOnly: true,
        minWidth: '180px',
      },
      actorType: {
        label: 'Actor-Typ',
        api: { itemField: 'actorType' },
        type: 'text',
        readOnly: true,
        minWidth: '120px',
      },
      actorId: {
        label: 'Actor',
        api: { itemField: 'actorId' },
        type: 'text',
        readOnly: true,
        minWidth: '280px',
        listDisplayValue: (value, item) => formatActorCell(item),
      },
      impersonatorId: {
        label: 'Identitätswechsel durch',
        api: { itemField: 'impersonatorId' },
        type: 'text',
        readOnly: true,
        minWidth: '260px',
        listDisplayValue: value => (value ? formatActorDisplay('user', value) : '—'),
      },
      requestId: {
        label: 'Request-ID',
        api: { itemField: 'requestId' },
        type: 'text',
        readOnly: true,
        minWidth: '170px',
      },
      changedKeys: {
        label: 'Geänderte Felder',
        api: { itemField: 'changedKeys' },
        type: 'text',
        readOnly: true,
        minWidth: '260px',
        listDisplayValue: value =>
          Array.isArray(value) && value.length > 0 ? value.slice(0, 4).join(', ') : '—',
      },
      tags: {
        label: 'Tags',
        api: { itemField: 'tags' },
        type: 'text',
        readOnly: true,
        minWidth: '160px',
        listDisplayValue: value =>
          Array.isArray(value) && value.length > 0 ? value.join(', ') : '—',
      },
    }),
    [formatActorCell, formatActorDisplay]
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
      <EditableList
        title='Audit-Log'
        resourceType='auditLog'
        schema={auditLogSchema}
        searchPlaceholder='Audit-Log durchsuchen'
        parentIsEditable={false}
        options={{
          disableCreate: true,
          disableDelete: true,
          disableDetailView: true,
          listConfig: {
            endpoint: '/api/v1/auditLog',
            resultsKey: 'auditLogs',
            pageParam: 'page',
            limitParam: 'limit',
          },
          serverSideFiltering: true,
          serverSideSorting: true,
        }}
        listParams={listParams}
        renderTopControlsStart={() => (
          <Box
            sx={{
              flex: '1 1 100%',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  select
                  fullWidth
                  label='Suchfeld'
                  value={searchField}
                  onChange={e => setSearchField(e.target.value)}
                >
                  <MenuItem value='action'>Aktion</MenuItem>
                  <MenuItem value='entityType'>Entity</MenuItem>
                  <MenuItem value='entityId'>Entity-ID</MenuItem>
                  <MenuItem value='actorId'>Actor-ID</MenuItem>
                  <MenuItem value='impersonatorId'>Identitätswechsel durch (ID)</MenuItem>
                  <MenuItem value='requestId'>Request-ID</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Suchbegriff'
                  inputRef={searchInputRef}
                  placeholder='z. B. USER_UPDATED'
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyFilters();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  select
                  fullWidth
                  label='Level'
                  value={levelFilter}
                  onChange={e => setLevelFilter(e.target.value)}
                >
                  <MenuItem value=''>Alle</MenuItem>
                  <MenuItem value='1'>1</MenuItem>
                  <MenuItem value='2'>2</MenuItem>
                  <MenuItem value='3'>3</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  select
                  fullWidth
                  label='Actor-Typ'
                  value={actorTypeFilter}
                  onChange={e => setActorTypeFilter(e.target.value)}
                >
                  <MenuItem value=''>Alle</MenuItem>
                  <MenuItem value='user'>user</MenuItem>
                  <MenuItem value='service'>service</MenuItem>
                  <MenuItem value='system'>system</MenuItem>
                  <MenuItem value='migration'>migration</MenuItem>
                  <MenuItem value='unknown'>unknown</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  select
                  fullWidth
                  label='Identitätswechsel'
                  value={impersonatedFilter}
                  onChange={e => setImpersonatedFilter(e.target.value)}
                >
                  <MenuItem value=''>Alle</MenuItem>
                  <MenuItem value='true'>Nur Identitätswechsel</MenuItem>
                  <MenuItem value='false'>Ohne Identitätswechsel</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Von'
                  type='date'
                  inputRef={fromInputRef}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} lg={2}>
                <TextField
                  size='small'
                  fullWidth
                  label='Bis'
                  type='date'
                  inputRef={toInputRef}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        renderTopControlsEnd={() => (
          <Stack
            direction='row'
            spacing={1}
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: 0,
              justifyContent: { xs: 'stretch', sm: 'flex-start' },
              '& > .MuiButton-root': { xs: { flex: '1 1 auto' } },
            }}
          >
            <Button variant='contained' size='small' onClick={applyFilters}>
              Filtern
            </Button>
            <Button variant='outlined' size='small' onClick={resetFilters}>
              Zurücksetzen
            </Button>
          </Stack>
        )}
        renderRowActions={item => (
          <Tooltip title='Details'>
            <IconButton size='small' onClick={() => openDetails(item)}>
              <VisibilityIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        visibleColumnKeys={[
          'createdAt',
          'level',
          'action',
          'entityType',
          'entityId',
          'actorType',
          'actorId',
          'changedKeys',
          'tags',
        ]}
      />

      <Dialog open={detailDialog.open} onClose={closeDetails} fullWidth maxWidth='lg'>
        <DialogTitle>Audit-Log Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography variant='body2'>Lade Details...</Typography>
          ) : (
            <Box>
              <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
                {detailDialog.item?._id || ''}
              </Typography>
              <Box
                component='pre'
                sx={{
                  m: 0,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 1,
                  backgroundColor:
                    theme.palette.mode === 'light'
                      ? theme.palette.grey[100]
                      : alpha(theme.palette.common.white, 0.04),
                  border: `1px solid ${theme.palette.divider}`,
                  overflowX: 'auto',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  maxHeight: { xs: 'calc(100vh - 220px)', sm: 'none' },
                }}
              >
                {JSON.stringify(detailDialog.data || detailDialog.item || {}, null, 2)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: isSmallScreen ? 1.5 : 2, py: 2 }}>
          <Button onClick={closeDetails}>Schließen</Button>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={() => openDetails(detailDialog.item)}
            disabled={!detailDialog.item || detailLoading}
          >
            Neu laden
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AuditLogView;

